import { logger } from "@/utils/logger";
import {
    Account,
    Address,
    BankAccount,
    ContactType,
    IContactMechanism,
    PartyAddress,
    PartyClassification,
    PartyContactCorrespondent,
    PartyContactMechanism,
    PartyCorrespondent,
    PartyCorrespondentStatus,
    Person,
    Picklist,
    Relation,
    Role,
    Third,
    ThirdType,
} from "@/types";
import {
    deepClone,
    deleteAttributes,
    deleteAttributesRecursively,
    deleteNonRequiredAttributes,
    getOperator,
    globalConfig,
    headers,
    Masks,
    mountSelectFields,
    Paths,
    unmountSelectFieldsV2,
    UON,
    upsert,
    upsertWithDataMessageReturn,
    upsertWithDataReturn
} from "@/utils";
import {formatDate, formatDateLocale, normalizeRelationValidity} from "@/utils/configuration/formatters-config";
import {api} from "@/auth/api";
import {settings} from "@/settings";
import store from "@/store";
import {buildPostalCode, saveFile} from "./offerService";
import { applyPersonNationalityFlag, getPersonNationalityCode } from "@/utils/nationalityFlag";
import {useApi} from "@/requests/useApi";
import {getThird} from "@/store/services/searchThird";
import moment from "moment/moment";
import {buildPreferences} from "@/commons/party-middleware";
import axios from "axios";
import i18n from "@/i18n";
import Notify from 'quasar/src/plugins/Notify.js';;
const system_uid = 'odm-party'

export async function saveThird(body: Third): Promise<any>{
    const relationsSnapshot = Array.isArray(body.relations)
        ? deepClone(body.relations).filter((item: Relation) => getRelationTypeUid(item) !== '')
        : [];
  
    const populateId = (party: Third | undefined): void => {
        if (body.id?.resourceUid === '') {
            body.id = {
                resourceUid: party?.resourceUid,
                objectType: party?.objectType,
                systemUid: party?.systemUid
            }
        }
    }

    function checkTypePerson() {
        return body.id?.objectType?.includes("frenchperson")
            || body.type?.id === ThirdType.PERSON;
    }

  if (checkTypePerson() && body.person) {
        delete body.person.type
        delete body.person.status
        try {
            const third = await savePerson(body);
            body.person = third?.person;
            body.relations = third?.relations;
            populateId(body.person);
        } catch (e: any) {
            if (e.message !== "VALIDATION_ERROR") throw e;
           // return body;
            return { validationFailed: true };
        }

        } else if (body.organization) {
        delete body.organization.manager
        delete body.organization.type
        delete body.organization.status
        try {
                const third = await saveOrganization(body);
                if(third){
                    body.organization = third?.organization;
                    body.relations = third?.relations;
                    populateId(body.organization);
                }else{  throw new Error("VALIDATION_ERROR");}
        } catch (e: any) {
            if (e.message !== "VALIDATION_ERROR") throw e;
          // return body;
            return { validationFailed: true };
        }
     }

    let third: any = {};
    const objectTypeArray = body.id?.objectType?.split('.');
    if (objectTypeArray && body.id?.resourceUid) {
        const type = `party-${objectTypeArray[objectTypeArray.length - 1]}`;
        const whenCreated = moment(new Date()).format('YYYY-MM-DD');
        third = await getThird(body.id.resourceUid, type, whenCreated);
        third = mergeJustSavedRelations(third, relationsSnapshot, body.relations);
    }
    return third;
    
   
}

async function savePerson(data: Third) {
    const body: Third = JSON.parse(JSON.stringify(data))
    if (body.person) {
        if (body.roles) {
            body.person.roles = buildRole(body.roles)
        }
        if (body.addresses) {
             const hasInvalidCountry = body.addresses.some(addr => {
             const countryName = addr?.address?.postalCode?.country?.objectType;
            return !countryName || countryName.trim() === "";
            });

                if (hasInvalidCountry) {
                    Notify.create({
                        timeout: 10000,
                        actions: [{ icon: 'close', color: 'white' }],
                        message: i18n.global.t("main.dialog.errorMessage.missingRequiredAtt"),
                        color: "negative",
                    });

                }
            const promises = body.addresses.map(partyAddress => saveAddresses(partyAddress.address, body.person?.roles))
            let livingSituation = ''
            let index = 0;
            const allAddressSaved = await Promise.all(promises)
            const personAddress = []
            for (const addr of allAddressSaved) {
                if (addr) {
                    const partyAddressRelationship = body?.addresses?.filter(address => address.address.resourceUid === addr)[0];
                    if (body.person?.addresses) {

                        livingSituation = (body.addresses[index] as any).livingSituation.resourceUid
                    }
                    const partyAddress: PartyAddress = {
                        resourceUid: partyAddressRelationship?.resourceUid || undefined,
                        daaq: "/",
                        address: {
                            resourceUid: addr,
                            objectType: 'odm.party.address'
                        },
                        livingSituation: livingSituation !== '' && livingSituation !== undefined ? {
                            resourceUid: livingSituation
                        } : null,
                        objectType: 'odm.party.partyaddress'
                    }
                    personAddress.push(partyAddress)
                }
                index++
            }
            body.person.addresses = personAddress
        }
      const requiredFields = [
            body.person?.title?.resourceUid,
            body.person?.firstName,
            body.person?.familyName,
            body.person?.birthDate,
            //body.person?.usageName,
            body.person?.birthplace
        ];
        if (requiredFields.some(field => field === "" || field === null || field === undefined)) {
            Notify.create({
                timeout: 10000,
                actions: [{ icon: 'close', color: 'white' }],
                message: i18n.global.t("main.dialog.errorMessage.missingRequiredAtt"),
                color: "negative",
            });
        }
        if (body.person.homemakers !== "") {
            buildBusinessDataPerson(body.person)
        }
        if (body.person.birthDate !== "") {
            buildBirthDatePerson(body.person)
        }
        const selectedNationality = getPersonNationalityCode(body.person);
        if (body.person.nationality?.type?.value || body.person.nationality?.resourceUid) {
            buildCitizenshipsPerson(body.person)
        } else {
            delete body.person.nationality
        }
        const businessDataCountry = (body.person.businessData as any)?.birthplaceCountry;
        const configCountry = body.person.birthplace?.type?.config?.country?.id;
        const foreignBirthCountry =
            businessDataCountry && businessDataCountry !== "FR"
                ? businessDataCountry
                : configCountry && configCountry !== "FR"
                    ? configCountry
                    : null;
        if (foreignBirthCountry) {
            if (!body.person.businessData) {
                body.person.businessData = {} as any
            }
            (body.person.businessData as any).birthplaceCountry = foreignBirthCountry;
            (body.person.businessData as any).birthplaceCity =
                (body.person.businessData as any).birthplaceCity
                ?? body.person.birthplace?.type?.config?.city
                ?? body.person.birthplace?.type?.label
                ?? "";
            (body.person.businessData as any).birthplaceZipCode = "99999";
            // Foreign birthplace lives entirely in businessData. Force the
            // birthplace to null (instead of omitting it) so the backend clears
            // any previous french postal code reference.
            body.person.birthplace = null as any
        } else {
            if (body.person.businessData) {
                delete (body.person.businessData as any).birthplaceCountry;
                delete (body.person.businessData as any).birthplaceCity;
                delete (body.person.businessData as any).birthplaceZipCode;
            }
            if (body.person.birthplace?.type?.id !== "") {
                buildBirthplace(body.person)
            } else {
                delete body.person.birthplace
            }
        }
        applyPersonNationalityFlag(body.person, selectedNationality);
        if (body.person.gender?.type?.value !== "") {
            buildGenderDataPerson(body.person)
        } else {
            delete body.person.gender
        }
        if (body.person.currentMaritalStatus) {
            buildSituation(body.person)
        } else {
            delete body.person.maritalStatuses
            body.person.maritalStatuses = []
        }
        if (body.person.revenues && body.person.revenues.length > 0 && body.person.revenues[0]?.analysisDate) {
            buildAnalysis(body.person)
        } else {
            delete body.person.revenues
            body.person.revenues = []
        }
        if (body.person.jobs && body.person.jobs.length > 0 && body.person.jobs[0].employerName) {
            await buildJobs(body.person, true)
            body.person.jobs?.map((job: any) => {
                if (job.jobLocation && !job.jobLocation.resourceUid) {
                    delete job.jobLocation
                }
                if (job.jobName) {
                    job.jobName = job.jobName;
                }
            })
        } else {
            delete body.person.jobs
            body.person.jobs = []
        }
        delete body.person.currentMaritalStatus
        delete body.person.currentMatrimonialRegime
        delete body.person.homemakers
        delete body.person.retirementPassage
        delete body.person.birthChildren
        delete body.person.removedSituation

        body.person.roles?.forEach((role: Role) => {
            role.preferences?.push(...role.facilityPreferences);
            delete role.facilityPreferences;
        });

        if (body.classifications) {
            body.person.classifications = buildClassification(body.classifications)
        }
        if (body.financialExposures && body.financialExposures.length > 0) {
            body.person.financialExposures = buildFinancialExposures(body.financialExposures)
        }else {
            delete body.financialExposures
            body.financialExposures = []
        }
        body.person.objectType = 'odm.party.party.person.frenchperson'

        if (body.person.contacts) {
            await buildPersonContacts(body.person);
        }
        const ids = body.person.ids
        if (ids && ids.length > 0) {
            body.person.ids?.forEach((item: any) => {
                item.emissionDate = item.emissionDate ? formatDateLocale(item.emissionDate, 'DD/MM/YYYY', 'fr') : '';
                item.validity.from = item.emissionDate;
                item.validity.until = item.validity.until ? formatDateLocale(item.validity.until, 'DD/MM/YYYY', 'fr') : '';
                item.documentType.resourceUid = item.documentType.type || item.documentType.resourceUid;

                delete item.documentType.type
                delete item.isDisabled
            });
        } else {
            delete body.person.ids
            body.person.ids = []
        }


        if (body.person.correspondents) {
            body.person.correspondents = body.person.correspondents.filter(correspondent => {
                return correspondent.firstName !== "" && correspondent.familyName !== "" && correspondent.qualificationType?.resourceUid !== "";
            });
            await buildCorrespondent(body.person.correspondents);
        }
        if (body.accounts) {
            const promises = body.accounts.map((item: Account) => saveAccounts(item.bankAccount))
            const entityList = await Promise.all(promises)
            body.person.accounts = []
            let i = 0
            for (const item of entityList) {
                if (item) {
                    const data: Account = {
                        objectType: 'odm.party.partybankaccount',
                        bankAccount: {
                            resourceUid: item,
                            objectType: 'odm.party.bankaccount'
                        },
                        daaq: '/',
                        resourceUid: body.accounts[i].resourceUid ? body.accounts[i].resourceUid : undefined
                    }
                    body.person.accounts.push(data);
                    i++
                }
            }
        }

        // The reference will be separated by Person and Organization on DB - because of that, we have this peace of code in both methods PP and PM
        if (!body.person.reference || body.person.reference === '') {
            body.person.reference = (await api().post(`/odm-configuration/api/1/odm-configuration/reference/generate/Party/`, {})).data.reference
        }

        if(body.person.financialExposures && body.person.financialExposures.length > 0) {
            body.person.financialExposures?.forEach((financialExposure: any) => {
                if (financialExposure.outstandingType && financialExposure.outstandingType.value === "") {
                    delete financialExposure.outstandingType
                }
                if (financialExposure.financialInstitution && financialExposure.financialInstitution.value === "") {
                    delete financialExposure.financialInstitution
                }
            })
        }else {
            delete body.person.financialExposures
            body.person.financialExposures = []
        }



        const clone = deepClone(body);


        cleanAttribute(body.person)
        body.person = await upsertWithDataReturn(system_uid, body.person, 'frenchperson');

        if (body.person?.resourceUid) {
            body.relations = await savePartyRelations(clone, body.person.resourceUid);
        }
        return body;
    }
}

function cleanAttribute(third: any) {
    delete third.relations
    delete third.codeApe
    delete third.type
    if (third.status?.resourceUid == "") {
        delete third.status
    }
    if (third.activity?.resourceUid == "") {
        delete third.activity
    }
}


export function buildCitizenshipsPerson(person: any) {
    const citizenResourceUid = (person.citizenships && person.citizenships.length > 0 && person.citizenships[0].resourceUid) ? person.citizenships[0].resourceUid : undefined
    person.citizenships = []
    if (person.nationality.type?.value || person.nationality.resourceUid) {
        const citizenship = {
            country: {
                resourceUid: person.nationality.type?.value || person.nationality.resourceUid,
                objectType: 'odm.party.country'
            },
            daaq: store.state.authModule?.daaq,
            objectType: "odm.party.citizenship",
            resourceUid: citizenResourceUid
        }
        person.citizenships.push(citizenship)
    }
    delete person.nationality
}

function buildFinancialExposures(financialExposures: any) {
    const exposures: any[] = []
    if (financialExposures && financialExposures.length > 0) {
        financialExposures.map((item: any) => {
            const exposure =
            {
                resourceUid: item.resourceUid,
                objectType: item.objectType,
                systemUid: item.systemUid,
                daaq: item.daaq,
                grossOutstanding: {
                    amount: item.grossOutstanding,
                    currency: "EUR"
                },
                netOutstanding: {
                    amount: item.netOutstanding,
                    currency: "EUR"
                },
                residualValue: {
                    amount: item.residualValue,
                    currency: "EUR"
                },
                flagFinancialPool: item.flagFinancialPool,
                flagRiskPool: item.flagRiskPool,
                shareFinancialPool: item.shareFinancialPool,
                shareRiskPool: item.shareRiskPool,
                effectiveDate: item.effectiveDate ? formatDateLocale(item.effectiveDate, 'DD/MM/YYYY', 'fr') : '',
                contractReference: item.contractReference,
                contractValidity: {
                    from: item.contractValidity.from ? formatDateLocale(item.contractValidity.from, 'DD/MM/YYYY', 'fr') : '',
                    until: item.contractValidity.until ? formatDateLocale(item.contractValidity.until, 'DD/MM/YYYY', 'fr') : '',
                },
                outstandingType: item.outstandingType?.value ? item.outstandingType?.value : item.outstandingType,
                financialInstitution: item.financialInstitution?.value ? item.financialInstitution?.value : item.financialInstitution,
                businessData: item.businessData,
            }
            exposures.push(exposure)
        })

        return exposures;
    }
    else {
        return []
    }

}

export function buildBirthDatePerson(person: any) {
    person.birthDate = person.birthDate ? formatDateLocale(person.birthDate, 'DD/MM/YYYY', 'fr') : moment().format(Masks.dateMask)
    delete person.age
}

export function buildBusinessDataPerson(person: any) {
    person.businessData = {
        ...(person.businessData || {}),
        homemakers: person.homemakers,
        retirementPassage: person.retirementPassage,
        birthChildren: person.birthChildren,
    }
}
export function buildGenderDataPerson(person: any) {
    const gender = {
        resourceUid: person.gender.resourceUid,
        objectType: 'odm.party.gender',
    }
    if(!person.gender?.type?.value &&  person?.title?.resourceUid){
        gender.resourceUid =   person?.title?.resourceUid === "M" ? "MALE":"FEMALE";
    } 
    delete person.gender
    person.gender = gender
}

export function buildBirthplace(person: any) {
  try {
    let birthplace: any = {
        resourceUid:
            person.birthplace?.postalCode?.value
            ?? person.birthplace?.postalCode?.postal_code
            ?? person.birthplace?.resourceUid
            ?? person.birthplace?.type?.id,
      objectType: "odm.party.postalcode.frenchpostalcode",
      systemUid: "odm-party",
    };
    if (typeof birthplace.resourceUid === "object") {
      birthplace = {
        resourceUid: birthplace.resourceUid.id || birthplace.resourceUid.value,
        objectType: "odm.party.postalcode.frenchpostalcode",
        systemUid: "odm-party",
      };
    }

    delete person.birthplace;
    person.birthplace = birthplace;
  } catch (error) {
    logger.error("Error updating birthplace:", error);
  }
}



export function buildSituation(person: any) {
    let maritalStatusesUID = null
    if (person.maritalStatuses[0] && person.maritalStatuses[0].resourceUid) {
        maritalStatusesUID = person.maritalStatuses[0].resourceUid
    }
    person.maritalStatuses = []
    if (!!person.currentMaritalStatus.resourceUid && !!person.currentMatrimonialRegime.resourceUid) {
        let maritalStatuses: any = {}
        if (maritalStatusesUID) {
            maritalStatuses = {
                maritalStatus: {
                    resourceUid: person.currentMaritalStatus.resourceUid,
                    objectType: 'odm.party.maritalstatus',
                },
                matrimonialRegime: {
                    resourceUid: person.currentMatrimonialRegime.resourceUid,
                    objectType: 'odm.party.matrimonialregime',
                },
                daaq: store.state.authModule?.daaq,
                resourceUid: maritalStatusesUID,
                objectType: "odm.party.personmaritalstatus"
            }
        } else {
            maritalStatuses = {
                maritalStatus: {
                    resourceUid: person.currentMaritalStatus.resourceUid,
                    objectType: 'odm.party.maritalstatus',
                },
                matrimonialRegime: {
                    resourceUid: person.currentMatrimonialRegime.resourceUid,
                    objectType: 'odm.party.matrimonialregime',
                },
                daaq: store.state.authModule?.daaq,
                resourceUid: maritalStatusesUID,
                objectType: "odm.party.personmaritalstatus"
            }
        }

        person.maritalStatuses.push(maritalStatuses)
    }
    delete person.currentMaritalStatus
    delete person.currentMatrimonialRegime
}

export function buildAnalysis(person: any) {
    const analyse: any = []
    person
        .revenues
        .filter((revenue: any) => revenue.analysisDate !== "" && !revenue.resourceUid)
        .forEach((revenue: any) => {
            const detailAnalyse: any = []
            revenue.detail.forEach((detailItem: any) => {
                if (detailItem.nature.resourceUid || detailItem.nature.type) {
                    const detail = {
                        nature: {
                            resourceUid: detailItem.nature.resourceUid || detailItem.nature.type,
                            objectType: 'odm.party.expenseincometype'
                        },
                        periodicity: {
                            resourceUid: detailItem.periodicity.resourceUid,
                            objectType: 'odm.party.expenseincomeperiodicity'
                        },
                        value: {
                            currency: "EUR",
                            amount: detailItem.value
                        },
                        label: detailItem.type,
                        validity: {
                            from: detailItem.validity.from ? formatDateLocale(detailItem.validity.from, 'YYYY-MM-DD', 'fr') : '',
                            until: detailItem.validity.until ? formatDateLocale(detailItem.validity.until, 'YYYY-MM-DD', 'fr') : '',
                        },
                        daaq: store.state.authModule?.daaq,
                        objectType: "odm.party.expenseincome"
                    }
                    detailAnalyse.push(detail)
                }
            })
            analyse.push({
                label: '',
                analysisDate: revenue.analysisDate ? formatDateLocale(revenue.analysisDate, 'YYYY-MM-DD', 'fr') : '',
                status: {
                    resourceUid: 'NEW',
                    objectType: 'odm.party.revenuesanalysisstatus'
                },
                yearlyExpensesTotal: {
                    currency: "EUR",
                    amount: 0
                },
                yearlyIncomeTotal: {
                    currency: "EUR",
                    amount: 0
                },
                daaq: store.state.authModule?.daaq,
                objectType: "odm.party.revenuesanalysis",
                detail: detailAnalyse
            })
        })
    delete person.analysis
    deleteAttributesRecursively(person.revenues, ['type'])
    if (analyse.length > 0) {
        person.revenues = analyse
    }

    person
        .revenues
        .filter((r: any) => !!r.resourceUid)
        .forEach((r: any) => {
            delete r.isDisabled
            r.analysisDate = r.analysisDate ? formatDateLocale(r.analysisDate, 'YYYY-MM-DD', 'fr') : '',
                r.detail.forEach((d: any) => {
                    d.daaq = store.state.authModule?.daaq,
                        d.value = {
                            currency: "EUR",
                            amount: d.value
                        },
                        d.validity = {
                            from: d.validity.from ? formatDateLocale(d.validity.from, 'YYYY-MM-DD', 'fr') : '',
                            until: d.validity.until ? formatDateLocale(d.validity.until, 'YYYY-MM-DD', 'fr') : '',
                        }
                })
        })
}

export async function buildJobs(person: any, saving?: boolean, daaqResourceUid?: string, isToOrchestration?: boolean | false) {
    const jobsPerson: any = []
    let addressId: any
    let address: any
    const jobsList = person.jobs.filter((item: any) => !!item.employerName)
    for (const el of jobsList) {
        if (saving) {
            unmountSelectFieldsV2(el, ['occupationType', 'activity', 'positionType', 'contractType'])
            if (el.jobLocation) {
                deleteAttributes(el.jobLocation, ['type']);
            }
            addressId = el.jobLocation  ? await saveAddresses(el.jobLocation, undefined, false, isToOrchestration) : null;
        } else {
            mountSelectFields(el, ['occupationType', 'activity', 'positionType', 'contractType'])
            if (el.jobLocation) {
                const result = await useApi(UON.SystemUid.odmParty, 'address').get(el.jobLocation.resourceUid);
                if (result.response) {
                    address = result.response
                    el.jobLocation.postalCode = await buildPostalCode(address.postalCode.resourceUid);
                }
            }
        }
        jobsPerson.push({
            businessData: el.businessData,
            nbYearExpereinceActivity: el.nbYearExpereinceActivity,
            nbYearExpereincePosition: undefined,
            employer: null,
            occupationType: (el.occupationType?.resourceUid || el.occupationType?.type?.value) ? el.occupationType : null,
            activity: el.activity,
            positionType: el.positionType,
            employerName: el.employerName,
            jobName: el.jobName,
            nbHour: el.nbHour ? Number(el.nbHour) : undefined,
            validity: {
                from: el.validity?.from ? saving ? isDateValid(el.validity?.from) ? formatDateLocale(el.validity?.from, 'DD/MM/YYYY', 'fr') : formatDateLocale(el.validity?.from, 'YYYY-MM-DD', 'fr') : el.validity?.from : '',
                until: el.validity?.until ? saving ? isDateValid(el.validity?.until) ? formatDateLocale(el.validity?.until, 'DD/MM/YYYY', 'fr') : formatDateLocale(el.validity?.until, 'YYYY-MM-DD', 'fr') : el.validity?.until : '',
            },
            employerCreationDate: el.employerCreationDate ? saving ? isDateValid(el.employerCreationDate) ? formatDateLocale(el.employerCreationDate, 'DD/MM/YYYY', 'fr') : formatDateLocale(el.employerCreationDate, 'YYYY-MM-DD', 'fr') : el.employerCreationDate : '',
            principal: el.principal,
            tenureDate: el.tenureDate ? saving ? isDateValid(el.tenureDate) ? formatDateLocale(el.tenureDate, 'DD/MM/YYYY', 'fr') : formatDateLocale(el.tenureDate, 'YYYY-MM-DD', 'fr') : el.tenureDate : '',
            daaq: daaqResourceUid ?? '/',
            objectType: "odm.party.job",
            resourceUid: el.resourceUid || undefined,
            contractType: (el.contractType?.resourceUid || el.contractType?.type?.value) ? el.contractType : null,
            jobLocation: el.jobLocation ? (saving && addressId !== null && !isToOrchestration) ? {
                        objectType: "odm.party.address",
                        systemUid: "odm-party",
                        resourceUid: addressId
                } : isToOrchestration ?
                {
                    objectType: "odm.party.address",
                    systemUid: "odm-party",
                    postalCode : {
                        resourceUid: el.jobLocation.postalCode.resourceUid || el.jobLocation.postalCode.postalCode
                    },
                    street: address?.street || el.jobLocation?.street,
                    streetNum: address?.streetNum || el.jobLocation?.streetNum,
                    resourceUid: el.jobLocation.resourceUid || undefined
                }
                : {
                    postalCode: {
                        resourceUid: el.jobLocation.postalCode.resourceUid || el.jobLocation.postalCode.postalCode || el.jobLocation.resourceUid,
                        type: el.jobLocation.postalCode && el.jobLocation.postalCode.type ? el.jobLocation.postalCode.type : null,
                        country: {
                            name: el.jobLocation.postalCode && el.jobLocation.postalCode.country && el.jobLocation.postalCode.country.name ? el.jobLocation.postalCode.country.name : '',
                        },
                        city: el.jobLocation.postalCode && el.jobLocation.postalCode.city ? el.jobLocation.postalCode.city : '',

                    },
                    street: address?.street,
                    streetNum: address?.streetNum,
                    resourceUid: el.jobLocation.resourceUid || undefined
                }
                : {
                    postalCode: {
                        type: '',
                        country: {
                            name: ''
                        },
                        city: ''

                    },
                    street: '',
                    streetNum: ''
                }
        })
    }
    delete person.jobs
    if (jobsPerson.length > 0) {
        person.jobs = jobsPerson
    }
    return person
}

function isDateValid(dateString: any) {
    
    // Expression régulière for the  format "DD/MM/YYYY"
    const regexDMY = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/;
    
    return regexDMY.test(dateString) 
}

/*const returnCorrectDate = (date: string, saving: boolean) => {
    return date ? saving ? useDate.sqlFormat(date) : date : '';
    return () => {
        if ( date ) {
            if ( saving ) {
                return useDate.sqlFormat(date)
            }
            else {
                return date
            }
        }
        else return ''
    }
}*/

async function buildCorrespondent(correspondents: PartyCorrespondent[]) {
    for (const c of correspondents) {
        if(!c.qualificationType?.resourceUid){
             c.qualificationType = null;
        }
        c.status.resourceUid = PartyCorrespondentStatus.ACTIVE
        const promises = c.contactCorrespondent.map((item: PartyContactCorrespondent) => saveContactCorrespondent(item))
        await Promise.all(promises)
        deleteAttributes(c, ['isDisabled'])
    }
}

function checkIfPostalCodesAreFulfilled(addresses: PartyAddress[]) {
    return addresses.filter(adr => adr.address.postalCode?.postalCode !== '').length === addresses.length;
}

async function saveOrganization(body: Third) {
    if (body.organization) {
    if (!body.organization.codeNACE?.resourceUid) {
      delete body.organization.codeNACE;
    }
    if (!body.organization.codeQualificationType?.resourceUid) {
      delete body.organization.codeQualificationType;
    }
        if (!body.organization.name || body.organization.name.trim() === "") {
            Notify.create({
                timeout: 10000,
                actions: [{ icon: 'close', color: 'white' }],
                message: i18n.global.t("main.dialog.errorMessage.missingRequiredAtt"),
                color: "negative",
            });
            return null;
        }
        if (typeof body.organization.name === "number" || /^\d+$/.test(body.organization.name.toString().trim())) {
                Notify.create({
                    timeout: 10000,
                    actions: [{ icon: 'close', color: 'white' }],
                    message: "le nom d'une organisation ne peut pas être un nombre",
                    color: "negative",
                });
                return null;
                }
        body.organization.objectType = 'odm.party.party.organization.frenchorganization'
        if (body.roles) {
            body.organization.roles = buildRole(body.roles)
        }
        if (body.organization.facilities) {
            const hasInvalidSiret = body.organization.facilities.some((facility:any) => {
            const siret = facility?.siret;
            return !siret || siret.trim() === "" || siret.length < 5;
            });

            if (hasInvalidSiret) {
            Notify.create({
                timeout: 10000,
                actions: [{ icon: 'close', color: 'white' }],
                message:`SIRET de l'etablissement - ${i18n.global.t("main.messageRequiredField")}`,
                color: "negative",
            });

            return null;
            }
            await buildFacilities(body.organization.facilities, body.organization.roles);
        }
        if (body.organization.contacts) {
            await buildPartyContacts(body.organization.contacts);
        }
        if (body.classifications) {
            body.organization.classifications = buildClassification(body.classifications)
        }
        if (body.financialExposures) {
            body.organization.financialExposures = buildFinancialExposures(body.financialExposures)
        }
        if (body.addresses && checkIfPostalCodesAreFulfilled(body.addresses)) {

            body.addresses.forEach(adresse => {
                delete adresse.livingSituation
            });

            const promises = body.addresses.map((partyAddress: PartyAddress) => savePartyAddresses(partyAddress, body.organization?.roles))
            const allPartyAddressSaved = await Promise.all(promises)
            if (body.organization.addresses) {
                body.organization.addresses.splice(0, body.organization.addresses.length)
            }
            body.organization.addresses?.push(...allPartyAddressSaved);

        }


        if (body.accounts) {
            const promises = body.accounts.map((item: Account) => saveAccounts(item.bankAccount))
            const entityList = await Promise.all(promises)
            body.organization.accounts = []
            let i = 0
            for (const item of entityList) {
                if (item) {
                    const data: Account = {
                        objectType: 'odm.party.partybankaccount',
                        bankAccount: {
                            resourceUid: item,
                            objectType: 'odm.party.bankaccount'
                        },
                        daaq: "/",
                        resourceUid: body.accounts[i].resourceUid ? body.accounts[i].resourceUid : undefined
                    }
                    body.organization.accounts.push(data);
                    i = i++
                }
                
            }
        }

        if (body.organization.correspondents && body.organization.correspondents.length >0) {
            body.organization.correspondents = body.organization.correspondents.filter((item : any)=> item.firstName)
            await buildCorrespondent(body.organization.correspondents);
        }
        if (body.organization.activitySector) {
            buildActivitySector(body.organization);
        }

        if (body.organization.balanceSheets) {
            for (const item of body.organization.balanceSheets) {

                let status = '';
                if (item.status?.id) {
                    status = item.status?.id
                }
                if (item.status?.value) {
                    status = item.status?.value
                }
                const country = item.country?.id;
                item.status = {
                    objectType: 'odm.party.balancesheetstatus',
                    resourceUid: status
                };
                item.country = {
                    objectType: 'odm.party.country',
                    resourceUid: country
                };

                if (item.period) {
                    item.period.until = item.period.until ? formatDateLocale(item.period.until, 'DD/MM/YYYY', 'fr') : ''
                    item.period.from = item.period.from ? formatDateLocale(item.period.from, 'DD/MM/YYYY', 'fr') : ''
                }
                if (item.detail) {
                    item.detail.forEach((dt: any) => {
                        const nature = dt.nature? dt.nature.id : '';
                        dt.nature = {
                            objectType: 'odm.party.balanceitemtype',
                            resourceUid: nature
                        };
                        dt.objectType = 'odm.party.balanceitem';
                        
                    });
                }
                item.daaq = '/'
                if (item.supportingDocument) {
                    if (item.supportingDocument && !item.supportingDocument.resourceUid) {

                        item.supportingDocument = await saveFile(item.supportingDocument).then((res) => {
                            return {
                                resourceUid: res.resourceUid,
                                objectType: "odm.filemngt.file",
                                systemUid: 'odm-filemngt'
                            }
                        })
                    }
                    else {
                        deleteNonRequiredAttributes(item.supportingDocument, ["objectType", "resourceUid", "system_uid"]);
                        item.supportingDocument.systemUid = 'odm-filemngt'
                    }
                }
              
             
            }
        }
        if (!body.organization.legalCategory?.resourceUid) {
            delete body.organization.legalCategory
        }

        // The reference will be separated by Person and Organization on DB - because of that, we have this peace of code in both methods PP and PM
        if (!body.organization.reference || body.organization.reference === '') {
            body.organization.reference = (await api().post(`/odm-configuration/api/1/odm-configuration/reference/generate/Party/`, {})).data.reference
        }
        formatCreationDate(body.organization)
        cleanAttribute(body.organization)
        body.organization.roles?.forEach((role: Role) => {
            role.preferences?.push(...role.facilityPreferences);
            delete role.facilityPreferences;
        });

        body.organization.financialExposures?.forEach((financialExposure: any) => {
            if (financialExposure.outstandingType.value === "") {
                delete financialExposure.outstandingType
            }
            if (financialExposure.financialInstitution.value === "") {
                delete financialExposure.financialInstitution
            }

        })

        body.organization = await upsertWithDataReturn(system_uid, body.organization, 'frenchorganization')

        if (body.organization?.resourceUid) {
            body.relations = await savePartyRelations(body, body.organization.resourceUid);
        }
    }
    return body;
}




const formatCreationDate = (organization: any) => {
    organization.creationDate = organization.creationDate ? formatDateLocale(organization.creationDate, 'DD/MM/YYYY', 'fr') : null
}
const buildActivitySector = (organization: any) => {

    if (organization.activitySector.resourceUid) {
        deleteNonRequiredAttributes(organization.activitySector, ['objectType', 'systemUid', 'resourceUid']);
    }
    else {

        organization.activitySector = null

    }

}


export function buildRole(roles: any) {
    const r: any = []
    roles.forEach((role: any) => {
        r.push({
            resourceUid: role.resourceUid,
            role: {
                resourceUid: role.role?.resourceUid,
                objectType: 'odm.party.role'
            },
            status: {
                resourceUid: role.status?.resourceUid || 'ACTIVATED',
                objectType: 'odm.party.rolestatus'
            },
            validity: {
                from: role.validity?.from ? formatDateLocale(role.validity.from, 'DD/MM/YYYY', 'fr') : formatDateLocale(moment().format('DD/MM/YYYY'), 'DD/MM/YYYY', 'fr'),
                until: role.validity?.until ? formatDateLocale(role.validity.until, 'DD/MM/YYYY', 'fr') : '',
            },
            nonSolicitation: role.nonSolicitation,
            daaq: "/",
            preferences: [],
            facilityPreferences: [],
            objectType: 'odm.party.partyrole'
        })
    })

    return r
}

function buildClassification(classifications: PartyClassification[]) {
    const classif: PartyClassification[] = []
    classifications.filter((item: PartyClassification) => !!item.partyClass?.resourceUid).forEach((classification: any) => {
        const partyClassification: any = {
            objectType: "odm.party.partyclassification",
            partyClass: {
                resourceUid: classification.partyClass?.resourceUid,
                objectType: "odm.party.partyclass",

            },
            validity: {
                from: classification.validity?.from ? formatDateLocale(classification.validity.from, 'DD/MM/YYYY', 'fr') : '',
                until: classification.validity?.until ? formatDateLocale(classification.validity.until, 'DD/MM/YYYY', 'fr') : '',
            },
            daaq: "/",
            classificationDate: classification.classificationDate ? formatDateLocale(classification.classificationDate, 'DD/MM/YYYY', 'fr') : '',

        }
        if (classification.resourceUid) {
            partyClassification.resourceUid = classification.resourceUid
        }
        classif.push(partyClassification)
    })
    return classif
}

export async function buildPersonContacts(person: Person) {
    const contacts = person.contacts?.filter((partyContactMechanism: PartyContactMechanism) => {
        const contactMechanism = partyContactMechanism.contactMechanism;
        return contactMechanism.emailAddress !== ""
            || contactMechanism.phoneNumber !== ""
            || contactMechanism.identifier !== ""
    })
    if (contacts && contacts.length > 0) {
        const promises = contacts.map((item: PartyContactMechanism) => saveContactMechanism(item.contactMechanism))
        await Promise.all(promises)
        person.contacts?.forEach((contact: PartyContactMechanism) => {
            deleteNonRequiredAttributes(contact, ["resourceUid", "contactMechanism", "daaq"]);
        })
    } else {
        delete person.contacts
        person.contacts = []
    }
}

export async function buildPartyContacts(contacts: PartyContactMechanism[], isToOrchestration?: boolean | false) {
    const promises = contacts.map(contact => saveContactMechanism(contact.contactMechanism, isToOrchestration))
    await Promise.all(promises)
    contacts.forEach(contact => {
        if (!isToOrchestration) {
            deleteNonRequiredAttributes(contact, ["resourceUid", "contactMechanism", "systemUid", "businessData"])
        } else {
            contact.facility = contact.facility || undefined
            contact.consent = contact.consent || []
        }
        contact.daaq = "/"
        contact.objectType = "odm.party.partycontactmechanism"
    })
}

const toDDMMYYYY = (value?: string) => {
    if (!value) return '';
    if (value.includes('/')) {
        return formatDateLocale(value, 'DD/MM/YYYY', 'fr', 'YYYY-MM-DD');
    }
    if (value.includes('-')) {
        return formatDateLocale(value, 'YYYY-MM-DD', 'fr', 'YYYY-MM-DD');
    }
    return formatDateLocale(value, 'DDMMYYYY', 'fr', 'YYYY-MM-DD');
};

export async function saveAccounts(item: BankAccount) {
    const body: BankAccount = { ...item }
    const status = body.status?.id
    const type = body.type?.id
    delete body.status
    delete body.type
    delete body.isPreferred
    body.status = { resourceUid: status }
    body.type = { resourceUid: type }
    if (item.validity) {
        body.validity = { until: '', from: '' }

        body.validity.until = item.validity.until ? toDDMMYYYY(item.validity.until) : ''
        body.validity.from = item.validity.from ? toDDMMYYYY(item.validity.from) : ''
    }
    body.iban = item.iban?.split(' ').join('')
    if (body.bic) {
        body.bic = body.bic.endsWith('XXX') ? body.bic : body.bic + 'XXX'
    }
    const responseData = await upsertWithDataMessageReturn(system_uid, body.iban, 'validateIban')

    if (responseData?.message === 'IBAN is correct') {
        return upsert(system_uid, body, 'frenchbankaccount')
    }
}

async function saveAddresses(address: Address, roles?: Role[], isFacility?: boolean, isToOrchestration?: boolean | false) {
    delete address.isDisabled;
    if (address.postalCode && !address.postalCode.resourceUid) {
        address.postalCode.resourceUid = address.postalCode.type?.value
    }
    if (address.postalCode && address.postalCode.type && address.postalCode.type.value) {
        address.postalCode.resourceUid = typeof address.postalCode.type?.value === 'string' ? address.postalCode.type?.value : address.postalCode.type?.value?.value
    }

    if (address.postalCode) {
        deleteNonRequiredAttributes(address.postalCode, ["resourceUid"]);
    }


    if (address.street || (address.postalCode?.resourceUid)) {
        const clonedAddress = deepClone(address);

        const isString = typeof address.type?.resourceUid === 'string';

        let addressId
        if (isToOrchestration) {
            addressId = address.resourceUid ? address.resourceUid : undefined
            if (roles && roles.length > 0 && !isString) {
                for (const role of roles) {
                    const preferences = isFacility ? role.facilityPreferences : role.preferences;
                    const params = {
                        preferences: preferences,
                        partyAddress: address,
                        addressUID: addressId
                    }
                    if (isFacility) {
                        role.facilityPreferences = await buildPreferences(params, isToOrchestration);
                    }
                    else {
                        role.preferences = await buildPreferences(params, isToOrchestration);
                    }
                    role.nonSolicitation = role.nonSolicitation || false;
                    role.geographicZone = role.geographicZone || undefined;
                }

            }
            if (!isString) {
                delete address.type;
            }
            return addressId;
        } else {
            if (!isString) {
                delete address.type;
            }
            if(address.type){
                deleteAttributesRecursively(address.type, ["type"]);
            }
            addressId = await upsert(system_uid, address, 'address')
            if (roles && roles.length > 0 && !isString) {
                for (const role of roles) {
                    const preferences = isFacility ? role.facilityPreferences : role.preferences;
                    const params = {
                        preferences: preferences,
                        partyAddress: clonedAddress,
                        addressUID: addressId
                    }
                    if (isFacility) {
                        role.facilityPreferences = await buildPreferences(params);
                    }
                    else {
                        role.preferences = await buildPreferences(params);
                    }
                }

            }
           
            
        }
        return addressId;

    }
}

export async function savePartyAddresses(item: PartyAddress, roles?: any, isToOrchestration?: boolean | false) {
    const addressId = await saveAddresses(item.address, roles, false, isToOrchestration);

    if (isToOrchestration) {
        item.address.resourceUid = addressId
        item.address.objectType = 'odm.party.address'
        item.livingSituation = item.livingSituation?.resourceUid ? item.livingSituation.resourceUid : null
    } else {
        item.address = {
            resourceUid: addressId,
            objectType: 'odm.party.address',
        }
        // deleteAttributesRecursively(item.address.type, ["type"]);
        // delete item.address.type?.type;
    }

    item.objectType = 'odm.party.partyaddress'
    item.daaq = "/"

    deleteNonRequiredAttributes(item, ["resourceUid", "objectType", "address", "daaq", "livingSituation"]);

    return item;
}

async function buildFacilities(body: any[], roles?: Role[]) {
    let index = 0
    for (const facility of body) {
        if (facility.siret) {
            delete facility.isDisabled;
            delete facility.activity;
            delete facility.livingSituation;
            facility.daaq = '/'
            const partyAddresses = new Array<PartyAddress>();
            partyAddresses[0] = {
                daaq: "/",
                address: facility.address
            };

            if (facility.address && checkIfPostalCodesAreFulfilled(partyAddresses)) {
                facility.address.resourceUid = await saveAddresses(facility.address, roles, true);
                deleteNonRequiredAttributes(facility.address, ["objectType", "systemUid", "resourceUid"]);
            }
            if (facility.address?.resourceUid === undefined) {
                facility.address = null;
            }

        } else {
            body.splice(index, 1);
        }
        index++
    }
}

async function buildPartyRelations(relations: Relation[]) {
    const savedRelations = [];
    for (const relation of relations) {
        normalizeRelationValidity(relation.validity);
        const { response } = await useApi(UON.SystemUid.odmParty, Paths.partyRelation).upsert(relation);
        savedRelations.push(response);
    }
    return savedRelations;
}

const saveContactMechanismType = async (contactMechanism: IContactMechanism, type: string, isToOrchestration?: boolean) => {
    let parentResourceUid = Paths.contactMechanism.parentResourceUid.get(type)
    Object.values(ContactType).forEach(contactType => {
        if (contactType === type) {
            parentResourceUid = Paths.contactMechanism.parentResourceUid.get(type)
        }
    })
    if (parentResourceUid) {
        delete contactMechanism.blocked
        if (isToOrchestration) {
            return contactMechanism.resourceUid ? contactMechanism.resourceUid : ""
        }
        return await upsert(system_uid, contactMechanism, parentResourceUid)
    }
}

async function saveContactMechanism(contactMechanism: IContactMechanism, isToOrchestration?: boolean | false) {
    if (contactMechanism){
        if (contactMechanism.validity) {
            if (contactMechanism.validity.from) {
                contactMechanism.validity.from = toDDMMYYYY(contactMechanism.validity.from);
            }
            if (contactMechanism.validity.until) {
                contactMechanism.validity.until = toDDMMYYYY(contactMechanism.validity.until);
            }
        }
        delete contactMechanism.isDisabled;
        const typeId = contactMechanism.type?.id
        if (contactMechanism.type && contactMechanism.type.config) {
            contactMechanism.objectType = contactMechanism.type.config.objectType ? contactMechanism.type.config.objectType : contactMechanism.objectType;
            if (contactMechanism.type?.config.fields) deleteNonRequiredAttributes(contactMechanism, contactMechanism.type?.config.fields);
        }
        if (typeId) {
            deleteAttributesRecursively(contactMechanism, ['type'])
            contactMechanism.resourceUid = await saveContactMechanismType(contactMechanism, typeId, isToOrchestration);
        }
        if (isToOrchestration) {
            if (contactMechanism.resourceUid === '') contactMechanism.resourceUid = undefined
            return contactMechanism
        }
        deleteNonRequiredAttributes(contactMechanism, ["objectType", "resourceUid"]);
        }
    return contactMechanism;
}

export async function saveContactCorrespondent(item: PartyContactCorrespondent, isToOrchestration?: boolean | false) {
    await saveContactMechanism(item.contactMechanism, isToOrchestration)
    deleteNonRequiredAttributes(item, ["objectType", "resourceUid", "contactMechanism", "daaq", "businessData"]);
    if (!item?.daaq) item.daaq = '/';
    return item;
}

function getRelationTypeUid(item: Relation): string {
    return item.relationType?.type?.value || item.relationType?.resourceUid || '';
}

function isSameRelation(left: any, right: any): boolean {
    if (left?.resourceUid && right?.resourceUid) {
        return String(left.resourceUid) === String(right.resourceUid);
    }
    return getRelationTypeUid(left) === getRelationTypeUid(right) &&
        left?.partyAsc?.resourceUid === right?.partyAsc?.resourceUid &&
        left?.partyDesc?.resourceUid === right?.partyDesc?.resourceUid;
}

/** Relations are read back from a replicated view that lags behind the write, so the rows this
 *  save just created are usually absent from the reloaded party. Add them back, in the shape the
 *  form binds to (`type.value` combos), so they stay visible without leaving the screen. */
function mergeJustSavedRelations(third: any, snapshot: Relation[], persisted: any): any {
    if (!third || !snapshot?.length) {
        return third;
    }

    const currentUid =
        third.id?.resourceUid ||
        third.person?.resourceUid ||
        third.organization?.resourceUid;
    const persistedRelations = Array.isArray(persisted) ? persisted : [];
    const reloaded = Array.isArray(third.relations) ? third.relations : [];

    const readUid = (value: any): string | undefined =>
        typeof value === 'string' ? value : value?.resourceUid;

    const missing = snapshot
        .map((rel: any, index: number) => {
            const typeUid = getRelationTypeUid(rel);
            const direction = rel.relation?.type?.value || rel.relation?.resourceUid || '';
            const partyAscUid = rel.partyAsc?.resourceUid || currentUid;
            const partyDescUid = rel.partyDesc?.resourceUid || currentUid;
            // `buildPartyRelations` upserts in the order it received, so the responses line up.
            const saved = persistedRelations.length === snapshot.length
                ? persistedRelations[index]
                : persistedRelations.find((item: any) =>
                    readUid(item?.relationType) === typeUid &&
                    readUid(item?.partyAsc) === partyAscUid &&
                    readUid(item?.partyDesc) === partyDescUid
                );
            return {
                ...rel,
                resourceUid: saved?.resourceUid || rel.resourceUid,
                relation: {
                    objectType: 'odm.party.relation',
                    type: { value: direction, label: '' }
                },
                relationType: {
                    objectType: 'odm.party.relationtype',
                    resourceUid: typeUid,
                    type: { value: typeUid, label: '' }
                },
                partyAsc: { ...(rel.partyAsc || {}), resourceUid: partyAscUid },
                partyDesc: { ...(rel.partyDesc || {}), resourceUid: partyDescUid },
            };
        })
        .filter((rel: any) => !reloaded.some((loaded: any) => isSameRelation(loaded, rel)));

    if (!missing.length) {
        return third;
    }

    const merged = [...reloaded, ...missing];
    third.relations = merged;
    if (third.person) {
        third.person.relations = merged;
    }
    if (third.organization) {
        third.organization.relations = merged;
    }
    return third;
}

function isEmptyResourceUid(value: unknown): boolean {
    return value === undefined || value === null || value === '';
}

async function savePartyRelations(body: Third, resourceUid: string) {
    if (body.relations) {
        const relations = deepClone(body.relations).filter((item: Relation) => getRelationTypeUid(item) !== '');

        relations.forEach((item: Relation) => {
            const relationTypeUid = getRelationTypeUid(item);
            item.relation = { resourceUid: item.relation?.type?.value || item.relation?.resourceUid };
            if (!item.relationType) {
                item.relationType = { objectType: 'odm.party.relationtype' };
            }
            item.relationType.resourceUid = relationTypeUid;

            if (item.validity) {
                normalizeRelationValidity(item.validity);
            }
            if (!item.partyAsc) {
                item.partyAsc = {};
            }
            if (!item.partyDesc) {
                item.partyDesc = {};
            }
            if (isEmptyResourceUid(item.partyAsc.resourceUid)) {
                item.partyAsc.resourceUid = resourceUid;
            }
            if (isEmptyResourceUid(item.partyDesc.resourceUid)) {
                item.partyDesc.resourceUid = resourceUid;
            }

            deleteNonRequiredAttributes(item.relationType, ['objectType', 'resourceUid']);
            deleteNonRequiredAttributes(item, ['objectType', 'systemUid', 'resourceUid', 'relationType', 'partyDesc', 'partyAsc', 'validity', 'sharingRate']);
        });

        const uids = store.getters["thirdModule/getDeletedRelationUids"] as string[];
        if (uids?.length) {
            await Promise.all(
                uids.map(uid =>
                    useApi(UON.SystemUid.odmParty, Paths.partyRelation).remove(uid)
                )
            );
            store.commit("thirdModule/setDeletedRelationUid", undefined);
            store.commit("thirdModule/clearDeletedRelationUids");
        }

        return await buildPartyRelations(relations);
    }
}

export const getPartyRelations = (resourceUid: string) => new Promise((resolve, reject) => {
    return api().get(settings.api_query_url, {
        headers: headers({
            qc: JSON.stringify({
                queryId: 'third-relations',
                offset: 0,
                limit: 100,
                parameters: { _resourceUid: resourceUid }
            })
        })
    }).then(response => {
        resolve(response.data);
    }).catch(err => {
        //FIXME: until debizium is fixed
        //reject(err)

        resolve([])
    })
})
// Valeurs de classification tiers (table party_party_class) = parametrage commun a toutes les offres,
// independant du Task / de la demande ouverts. Cache court + mutualisation des appels simultanes :
// evite 30+ requetes identiques a l'ouverture d'une action. Chaque appelant recoit une copie.
const PARTY_CLASS_CACHE_TTL_MS = 5 * 60 * 1000;
const partyClassCache = new Map<string, { expireAt: number, data: Picklist }>();
const partyClassInFlight = new Map<string, Promise<Picklist>>();
const copyPicklist = (picklist: Picklist): Picklist =>
    ({ ...picklist, items: (picklist.items || []).map((item: any) => ({ ...item })) });

export function clearPartyClassCache(): void {
    partyClassCache.clear();
}

export function getPartyClass(filter: any): Promise<Picklist> {
    const locale: any = i18n.global.locale;
    const cacheKey = JSON.stringify({ filter: filter ?? null, locale: locale?.value ?? locale });
    const cached = partyClassCache.get(cacheKey);
    if (cached && cached.expireAt > Date.now()) {
        return Promise.resolve(copyPicklist(cached.data));
    }
    let request = partyClassInFlight.get(cacheKey);
    if (!request) {
        request = fetchPartyClass(filter)
            .then((data) => {
                partyClassCache.set(cacheKey, { expireAt: Date.now() + PARTY_CLASS_CACHE_TTL_MS, data });
                return data;
            })
            .finally(() => partyClassInFlight.delete(cacheKey));
        partyClassInFlight.set(cacheKey, request);
    }
    return request.then(copyPicklist);
}

function fetchPartyClass(filter: any): Promise<Picklist> {
    return new Promise<Picklist>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'party-class-picklist',
                limit: -1,
                offset: 0
            }
        }
        qc_header.qc.parameters = {
            ...filter
        }
        const operators = []
        if (filter && filter.class_type_code != null) {
            operators.push(getOperator("party_party_class", "class_type_code", "EQUAL", `'${filter.class_type_code}'`, "class_type_code"))
            if(filter.id !== undefined){
                operators.push(getOperator("party_party_class", "id", "LIKE", globalConfig.formatters.getEqualValue(filter.id + '%'), "id"))
            } 
        }

        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
            qc_header.qc.otherOrders = null
        }

        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })

        axios.get(url, {
            headers: _headers
        }).then((res) => {
            const response = {
                systemUid: 'odm_party',
                items: res.data.map((resItem: any) => {
                    return {
                        value: resItem.id,
                        label: i18n.global.t(`demand.party.partyClass.${resItem.id}`),
                    };
                }),
            };
            resolve(response);
        })
            .catch((err) => reject(err));


    })
}

export async function saveFinancialExposureForKSIOP(body: any) {
    const daaqResourceUid = store?.state?.authModule?.daaqResourceUid || '/'
    const url = `/${UON.SystemUid.odmOrchestration}/business-api/1/${UON.SystemUid.odmOrchestration}/ksiop/ongoing/`;
    return (await api(daaqResourceUid).post(url, body)).data;
}
